import React, { useMemo } from 'react'
import { HiArrowLeft, HiDownload, HiExternalLink } from 'react-icons/hi'
import { Link, Navigate, useParams } from 'react-router-dom'
import { CaseStudyNavigation } from '../components/caseStudy/CaseStudyNavigation'
import { CaseStudySection } from '../components/caseStudy/CaseStudySection'
import { SEOHead } from '../components/seo/SEOHead'
import { Badge, Card, Container, Heading, Text } from '../components/ui'
import { useCaseStudy } from '../hooks/useCaseStudies'
import '../styles/CaseStudyPage.css'

/**
 * Individual Case Study page component displaying detailed technical analysis
 *
 * Features:
 * - Comprehensive case study content with structured sections
 * - Technical diagrams and code examples
 * - Left sidebar navigation for easy content browsing
 * - Key metrics and technology breakdown
 * - Related projects and call-to-action
 * - Mobile-responsive design with professional styling
 * - SEO optimization for technical content
 * - Accessible content structure and navigation
 */
const CaseStudyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { caseStudy, isLoading, error } = useCaseStudy(slug || '')

  // Generate section navigation data
  const navigationSections = useMemo(() => {
    if (!caseStudy) return []

    return [
      { id: 'overview', title: 'Overview', href: '#overview' },
      { id: 'technologies', title: 'Technologies', href: '#technologies' },
      ...caseStudy.sections.map(section => ({
        id: section.id,
        title: section.title,
        href: `#${section.id}`,
      })),
      { id: 'related', title: 'Related Projects', href: '#related' },
    ]
  }, [caseStudy])

  // Handle loading state
  if (isLoading) {
    return (
      <main className="page-main" role="main">
        <Container size="lg" padding="lg">
          <div className="case-study-loading">
            <Text size="lg" variant="secondary">
              Loading case study...
            </Text>
          </div>
        </Container>
      </main>
    )
  }

  // Handle error/not found
  if (error || !caseStudy) {
    return <Navigate to="/case-studies" replace />
  }

  const {
    title,
    subtitle,
    category,
    overview,
    keyMetrics,
    technologies,
    sections,
    readingTime,
    publishDate,
    downloadUrl,
    relatedProjectIds,
  } = caseStudy

  // Format category for display
  const categoryLabels: Record<string, string> = {
    enterprise: 'Enterprise Systems',
    architecture: 'Software Architecture',
    performance: 'Performance Optimization',
    'ai-integration': 'AI Integration',
    scalability: 'Scalability Solutions',
  }

  const categoryLabel = categoryLabels[category] || category

  // Format date for display
  const formattedDate = new Date(publishDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Group technologies by category
  const techByCategory = useMemo(() => {
    return technologies.reduce(
      (acc, tech) => {
        if (!acc[tech.category]) {
          acc[tech.category] = []
        }
        acc[tech.category].push(tech)
        return acc
      },
      {} as Record<string, typeof technologies>
    )
  }, [technologies])

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        page="case-study"
        path={`/case-studies/${slug}`}
        config={{
          seo: {
            title: title,
            description: subtitle,
          },
        }}
      />

      <main
        className="page-main case-study-page"
        id="main-content"
        tabIndex={-1}
        role="main"
        aria-label={`Case Study: ${title}`}
      >
        {/* Back Navigation - Full Width */}
        <div className="case-study-breadcrumb-container">
          <Container size="lg" padding="lg">
            <nav className="case-study-breadcrumb" aria-label="Breadcrumb">
              <Link
                to="/case-studies"
                className="breadcrumb-link"
                aria-label="Back to case studies"
              >
                <HiArrowLeft size={16} aria-hidden="true" />
                Back to Case Studies
              </Link>
            </nav>
          </Container>
        </div>

        {/* Main Layout with Sidebar */}
        <div className="case-study-layout">
          {/* Left Sidebar Navigation */}
          <aside className="case-study-sidebar" aria-label="Table of contents">
            <div className="sidebar-content">
              <CaseStudyNavigation sections={navigationSections} />
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="case-study-main-content">
            <Container size="lg" padding="lg">
              {/* Case Study Header */}
              <header className="case-study-header">
                {/* <div className="case-study-meta">
                  <Badge variant="secondary" size="sm">
                    {categoryLabel}
                  </Badge>
                  <div className="meta-items">
                    <div className="meta-item">
                      <HiClock size={14} aria-hidden="true" />
                      <Text size="sm" variant="tertiary">
                        {readingTime} read
                      </Text>
                    </div>
                    <div className="meta-item">
                      <HiCalendar size={14} aria-hidden="true" />
                      <Text size="sm" variant="tertiary">
                        {formattedDate}
                      </Text>
                    </div>
                  </div>
                </div> */}

                <Heading
                  as="h1"
                  size="4xl"
                  variant="gradient"
                  className="case-study-title"
                >
                  {title}
                </Heading>
                <Text
                  size="xl"
                  variant="secondary"
                  className="case-study-subtitle"
                >
                  {subtitle}
                </Text>

                {/* Download PDF if available */}
                {downloadUrl && (
                  <div className="case-study-actions">
                    <a
                      href={downloadUrl}
                      className="button button--outline button--md"
                      aria-label="Download case study as PDF"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <HiDownload size={16} aria-hidden="true" />
                      Download PDF
                    </a>
                  </div>
                )}
              </header>

              {/* Case Study Content */}
              <div className="case-study-content">
                {/* Overview Section */}
                <section id="overview" className="case-study-section">
                  <Heading as="h2" size="2xl" className="section-title">
                    Overview
                  </Heading>

                  <Card
                    variant="surface"
                    padding="lg"
                    className="overview-card"
                  >
                    <div className="overview-grid">
                      <div className="overview-item">
                        <Text
                          size="sm"
                          variant="secondary"
                          className="overview-label"
                        >
                          Challenge
                        </Text>
                        <Text size="base" variant="primary">
                          {overview.problem}
                        </Text>
                      </div>

                      <div className="overview-item">
                        <Text
                          size="sm"
                          variant="secondary"
                          className="overview-label"
                        >
                          Solution
                        </Text>
                        <Text size="base" variant="primary">
                          {overview.solution}
                        </Text>
                      </div>

                      <div className="overview-item">
                        <Text
                          size="sm"
                          variant="secondary"
                          className="overview-label"
                        >
                          Impact
                        </Text>
                        <Text size="base" variant="primary">
                          {overview.impact}
                        </Text>
                      </div>
                    </div>

                    <div className="overview-details">
                      <div className="detail-item">
                        <Text size="sm" variant="secondary">
                          Duration:
                        </Text>
                        <Text size="sm" variant="primary">
                          {overview.duration}
                        </Text>
                      </div>
                      <div className="detail-item">
                        <Text size="sm" variant="secondary">
                          Team:
                        </Text>
                        <Text size="sm" variant="primary">
                          {overview.team}
                        </Text>
                      </div>
                      <div className="detail-item">
                        <Text size="sm" variant="secondary">
                          Role:
                        </Text>
                        <Text size="sm" variant="primary">
                          {overview.role}
                        </Text>
                      </div>
                    </div>
                  </Card>
                </section>

                {/* Key Metrics */}
                {keyMetrics.length > 0 && (
                  <section className="case-study-section">
                    <Heading as="h2" size="2xl" className="section-title">
                      Key Results
                    </Heading>
                    <div className="metrics-grid">
                      {keyMetrics.map((metric, index) => (
                        <Card
                          key={index}
                          variant="surface"
                          padding="lg"
                          className="metric-card"
                        >
                          <div className="metric-value">{metric.value}</div>
                          <Text
                            size="sm"
                            variant="secondary"
                            className="metric-label"
                          >
                            {metric.label}
                          </Text>
                          {metric.description && (
                            <Text
                              size="xs"
                              variant="tertiary"
                              className="metric-description"
                            >
                              {metric.description}
                            </Text>
                          )}
                        </Card>
                      ))}
                    </div>
                  </section>
                )}

                {/* Technologies Section */}
                <section id="technologies" className="case-study-section">
                  <Heading as="h2" size="2xl" className="section-title">
                    Technologies & Tools
                  </Heading>

                  <Card variant="surface" padding="lg">
                    <div className="tech-categories">
                      {Object.entries(techByCategory).map(
                        ([categoryName, techs]) => (
                          <div key={categoryName} className="tech-category">
                            <Text
                              size="sm"
                              variant="secondary"
                              className="tech-category-title"
                            >
                              {categoryName.charAt(0).toUpperCase() +
                                categoryName.slice(1)}
                            </Text>
                            <div className="tech-tags">
                              {techs.map((tech, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  size="sm"
                                  className={`tech-tag tech-tag--${tech.category}`}
                                  title={tech.description}
                                >
                                  {tech.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </Card>
                </section>

                {/* Case Study Sections */}
                {sections.map(section => (
                  <CaseStudySection key={section.id} section={section} />
                ))}

                {/* Related Projects */}
                {relatedProjectIds && relatedProjectIds.length > 0 && (
                  <section id="related" className="case-study-section">
                    <Heading as="h2" size="2xl" className="section-title">
                      Related Projects
                    </Heading>

                    <Card variant="surface" padding="lg">
                      <Text
                        size="base"
                        variant="secondary"
                        className="related-description"
                      >
                        Explore related projects from my portfolio that
                        demonstrate similar technologies and problem-solving
                        approaches.
                      </Text>
                      <div className="related-actions">
                        <Link
                          to="/projects"
                          className="button button--primary button--md"
                        >
                          <HiExternalLink size={16} aria-hidden="true" />
                          View All Projects
                        </Link>
                      </div>
                    </Card>
                  </section>
                )}

                {/* Call to Action */}
                <section className="case-study-section case-study-cta">
                  <Card variant="surface" padding="lg" className="cta-card">
                    <div className="cta-content">
                      <Heading as="h2" size="xl" className="cta-title">
                        Interested in Similar Solutions?
                      </Heading>
                      <Text
                        size="base"
                        variant="secondary"
                        className="cta-description"
                      >
                        This case study demonstrates the depth of analysis and
                        architectural thinking I bring to complex technical
                        challenges. Let's discuss how I can help solve your
                        specific problems.
                      </Text>
                      <div className="cta-actions">
                        <a
                          href={`mailto:lucas@lucasstufflebeam.com?subject=Regarding Case Study: ${title}`}
                          className="button button--primary button--lg"
                          aria-label="Contact Lucas Stufflebeam about this case study"
                        >
                          Discuss This Case Study
                        </a>
                        <Link
                          to="/case-studies"
                          className="button button--outline button--lg"
                        >
                          View More Case Studies
                        </Link>
                      </div>
                    </div>
                  </Card>
                </section>
              </div>
            </Container>
          </div>
        </div>
      </main>
    </>
  )
}

export default CaseStudyPage
